package com.evoapps.evoshift.domain.timeline

import com.evoapps.evoshift.domain.market.payroll.PayrollFact
import com.evoapps.evoshift.domain.market.payroll.PayrollProfile
import com.evoapps.evoshift.domain.market.payroll.ProfilePeriod
import com.evoapps.evoshift.domain.market.payroll.profileOn
import com.evoapps.evoshift.domain.model.ContractPeriod
import com.evoapps.evoshift.domain.model.Engagement
import com.evoapps.evoshift.domain.model.FinancialEngagement
import com.evoapps.evoshift.domain.model.RatePeriod
import com.evoapps.evoshift.domain.model.StaffCheck
import com.evoapps.evoshift.domain.model.StaffCheckRecord
import com.evoapps.evoshift.domain.model.StaffRecord
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.minus
import kotlinx.datetime.plus

/*
 * SH-212: reading the timeline from its sources and writing an edited one back to them. Both
 * directions go through the same entries, so what is shown is exactly what is stored.
 */

internal fun day(text: String?): LocalDate? =
    text?.trim()?.take(10)?.takeIf { it.isNotEmpty() }?.let { runCatching { LocalDate.parse(it) }.getOrNull() }

private fun LocalDate.next(): LocalDate = plus(DatePeriod(days = 1))
private fun LocalDate.previous(): LocalDate = minus(DatePeriod(days = 1))

/** One payroll fact switched on or off in a [PayrollProfile]; [until] is its known end where it has one. */
internal fun PayrollProfile.with(fact: PayrollFact, on: Boolean, until: LocalDate?): PayrollProfile = when (fact) {
    PayrollFact.UNDER_26 -> copy(under26 = on, under26Until = until?.toString()?.takeIf { on })
    PayrollFact.STUDENT -> copy(student = on, studentUntil = until?.toString()?.takeIf { on })
    PayrollFact.TAX_RELIEF -> copy(taxReliefRequested = on)
    PayrollFact.COMMUTES -> copy(commutes = on)
    PayrollFact.VOLUNTARY_SICKNESS -> copy(voluntarySickness = on)
    PayrollFact.INSURED_ELSEWHERE -> copy(insuredElsewhereAtMinimumWage = on)
    PayrollFact.OVER_50 -> copy(over50 = on)
    PayrollFact.LONG_SERVICE -> copy(longService = on)
    PayrollFact.PPK -> copy(ppk = on)
    PayrollFact.PPK_REDUCED -> copy(ppkReducedRate = on)
}

/** SH-212: the from–to spans of one payroll fact over a person's dated payroll facts. */
object FactSpans {
    /** A stretch of days the fact applies: [from] null = from the start, [until] null = open-ended. */
    data class Span(val from: LocalDate?, val until: LocalDate?)

    private val KNOWN_ENDS = listOf(PayrollFact.UNDER_26, PayrollFact.STUDENT)

    /**
     * When [fact] applies among [periods] (their known ends applied, as the payroll reads them):
     * the maximal runs of days it is on.
     */
    fun of(periods: List<ProfilePeriod>, fact: PayrollFact): List<Span> {
        if (periods.isEmpty()) return emptyList()
        val sorted = periods.sortedBy { it.effectiveFrom }
        val days = mutableSetOf<String>()
        sorted.forEach { period ->
            days += period.effectiveFrom
            KNOWN_ENDS.forEach { known -> day(period.profile.knownEnd(known))?.let { days += it.next().toString() } }
        }
        fun onAt(d: String): Boolean = sorted.profileOn(d).facts.getValue(fact)
        val spans = mutableListOf<Span>()
        var start: String? = null
        var open = false
        days.sorted().forEach { d ->
            val on = onAt(d)
            if (on && !open) { start = d; open = true }
            if (!on && open) {
                spans += Span(day(start), day(d)?.previous())
                open = false
            }
        }
        if (open) spans += Span(day(start), null)
        return spans
    }

    /**
     * [periods] rewritten so that [fact] applies exactly on [spans] (sorted, not overlapping),
     * everything else as it was. The under-26 and student statuses keep their end as the known
     * end too (the employee form's "until" fields, older app versions); every fact also gets a
     * period off from the day after a span, so the history alone says when it ended. Adjacent
     * periods with the same facts are merged.
     */
    fun write(periods: List<ProfilePeriod>, fact: PayrollFact, spans: List<Span>, fallback: PayrollProfile = PayrollProfile()): List<ProfilePeriod> {
        val sorted = periods.sortedBy { it.effectiveFrom }.ifEmpty { listOf(ProfilePeriod("", fallback)) }
        val days = mutableSetOf<String>()
        sorted.forEach { days += it.effectiveFrom }
        spans.forEach { span ->
            days += span.from?.toString() ?: ""
            span.until?.let { days += it.next().toString() }
        }
        val rebuilt = days.sorted().map { d ->
            val raw = (sorted.lastOrNull { it.effectiveFrom <= d } ?: sorted.first()).profile
            val at = day(d)
            val span = spans.firstOrNull { s -> if (at == null) s.from == null else (s.from == null || at >= s.from) && (s.until == null || at <= s.until) }
            ProfilePeriod(d, raw.with(fact, span != null, span?.until))
        }
        val merged = mutableListOf<ProfilePeriod>()
        rebuilt.forEach { period -> if (merged.lastOrNull()?.profile != period.profile) merged += period }
        // The first period always applies from the start, as before any edit.
        return merged.mapIndexed { i, p -> if (i == 0 && sorted.first().effectiveFrom.isEmpty()) p.copy(effectiveFrom = "") else p }
    }
}

/**
 * SH-232: the months a person does not count in the labour cost, labour % and SPMH, as from–to
 * spans of whole months, read from SH-206's setting and its default ([LabourSource]), and the
 * setting written back so that exactly those months do not count.
 */
object LabourSpans {
    private fun monthIndex(month: String): Int? =
        month.takeIf { it.length == 7 && it[4] == '-' }?.let { m -> m.take(4).toIntOrNull()?.let { y -> m.drop(5).toIntOrNull()?.let { y * 12 + it - 1 } } }

    private fun monthOf(index: Int): String = "${index / 12}-${(index % 12 + 1).toString().padStart(2, '0')}"
    private fun monthOf(day: LocalDate): String = com.evoapps.evoshift.domain.budget.LabourInclusion.monthOf(day)
    private fun indexOfMonth(day: LocalDate): Int = day.year * 12 + day.month.ordinal
    private fun firstDay(index: Int): LocalDate = LocalDate(index / 12, index % 12 + 1, 1)
    private fun lastDay(index: Int): LocalDate = firstDay(index + 1).previous()

    /**
     * The months the answer can change in, from the first to the last: [today]'s and the hire's,
     * the setting's periods, the days without pay starting and ending, and [extra].
     */
    private fun window(source: LabourSource, today: LocalDate, hire: LocalDate?, extra: List<Int> = emptyList()): IntRange {
        val months = buildList<Int> {
            add(indexOfMonth(today))
            hire?.let { add(indexOfMonth(it)) }
            source.periods.forEach { p -> monthIndex(p.fromMonth)?.let { add(it) } }
            source.unpaid.forEach { p ->
                day(p.from)?.let { add(indexOfMonth(it)) }
                day(p.until)?.let { add(indexOfMonth(it.next())) }
            }
            addAll(extra)
        }
        return months.min()..months.max()
    }

    /** The runs of months [source] does not count in: from null = from the start, until null = open-ended. */
    fun of(source: LabourSource, today: LocalDate, hire: LocalDate?): List<FactSpans.Span> {
        val months = window(source, today, hire)
        val spans = mutableListOf<FactSpans.Span>()
        var start: Int? = null
        months.forEach { m ->
            val out = !source.countsIn(monthOf(m))
            if (out && start == null) start = m
            val begun = start
            if (!out && begun != null) {
                spans += FactSpans.Span(spanStart(source, begun, months.first), lastDay(m - 1))
                start = null
            }
        }
        start?.let { spans += FactSpans.Span(spanStart(source, it, months.first), null) }
        return spans
    }

    /** A run from the window's first month by default (no setting there) is "from the start". */
    private fun spanStart(source: LabourSource, month: Int, first: Int): LocalDate? =
        if (month == first && com.evoapps.evoshift.domain.budget.LabourInclusion.settingIn(source.periods, monthOf(month)) == null) null
        else firstDay(month)

    /**
     * The setting under which exactly the months of [spans] do not count: one period wherever
     * the wanted answer differs from what applies (the default where nothing is set). Empty:
     * the default everywhere (the setting goes).
     */
    fun write(source: LabourSource, spans: List<FactSpans.Span>, today: LocalDate, hire: LocalDate?): List<com.evoapps.evoshift.domain.budget.LabourInclusionPeriod> {
        val bounds = spans.flatMap { span -> listOfNotNull(span.from?.let(::indexOfMonth), span.until?.let { indexOfMonth(it) + 1 }) }
        val months = window(source, today, hire, bounds)
        fun out(m: Int): Boolean = spans.any { span ->
            (span.from == null || indexOfMonth(span.from) <= m) && (span.until == null || m <= indexOfMonth(span.until))
        }
        val periods = mutableListOf<com.evoapps.evoshift.domain.budget.LabourInclusionPeriod>()
        var setting: Boolean? = null
        months.forEach { m ->
            val wanted = !out(m)
            val applies = setting ?: source.defaultIn(monthOf(m))
            if (wanted != applies) {
                periods += com.evoapps.evoshift.domain.budget.LabourInclusionPeriod(monthOf(m), wanted)
                setting = wanted
            }
        }
        return periods.takeLast(com.evoapps.evoshift.domain.budget.LabourInclusion.MAX_PERIODS)
    }
}

/** SH-212: the timeline of [sources]. */
object TimelineReader {
    fun read(userId: String, workplaceId: String, sources: TimelineSources, today: LocalDate, reliefs: List<PayrollFact>): EmployeeTimeline {
        val entries = mutableListOf<TimelineEntry>()
        sources.engagement?.let { engagement ->
            entries += TimelineEntry(TimelineKind.HIRE, day(engagement.startDate))
            // SH-234: a re-activated engagement's old end is history, not an end of what runs now.
            if ((engagement.endDate != null || !engagement.isActive) && !sources.endIsStale(today)) {
                entries += TimelineEntry(TimelineKind.END, day(engagement.endDate))
            }
        }
        sources.financial?.let { terms ->
            terms.contractPeriods.forEach {
                entries += TimelineEntry(TimelineKind.CONTRACT, day(it.effectiveFrom), contractTypeId = it.contractTypeId, declaredHours = it.declaredNumberOfHours)
            }
            terms.ratePeriods.forEach { entries += TimelineEntry(TimelineKind.PAY, day(it.effectiveFrom), pay = it.compensation) }
            val profiles = terms.profilePeriods
            FactSpans.of(profiles, PayrollFact.STUDENT).forEach { entries += TimelineEntry(TimelineKind.STUDENT, it.from, it.until) }
            reliefs.forEach { fact ->
                FactSpans.of(profiles, fact).forEach { entries += TimelineEntry(TimelineKind.TAX_RELIEF, it.from, it.until, fact = fact) }
            }
        }
        val current = mutableMapOf<StaffCheck, String>()
        sources.staff?.let { record ->
            StaffCheck.entries.forEach { check ->
                record.records(check).forEach { entries += TimelineEntry(TimelineKind.of(check), it.from, it.validUntil) }
                record.current(check)?.let { current[check] = TimelineEntry(TimelineKind.of(check), it.from, it.validUntil).id }
            }
        }
        sources.notes.forEach { entries += TimelineEntry(TimelineKind.NOTE, it.on, text = it.text, noteId = it.id) }
        // SH-232: working without pay and not counting in the labour figures.
        sources.unpaid?.let { periods ->
            com.evoapps.evoshift.domain.budget.UnpaidWork.normalized(periods).forEach { entries += TimelineEntry(TimelineKind.UNPAID, day(it.from), day(it.until)) }
        }
        sources.labour?.let { labour ->
            val hire = day(sources.engagement?.startDate)
            LabourSpans.of(labour, today, hire).forEach { entries += TimelineEntry(TimelineKind.NOT_COUNTED, it.from, it.until) }
        }
        return EmployeeTimeline(
            userId = userId,
            workplaceId = workplaceId,
            // SH-250: redundant entries (older documents) are shown as one.
            entries = TimelineMerge.merged(entries),
            today = today,
            hasPayTerms = sources.financial != null,
            reliefs = reliefs,
            currentChecks = current,
            showsUnpaid = sources.unpaid != null,
            showsLabour = sources.labour != null
        )
    }
}

/** The engagement's dates as an edit leaves them. */
data class EngagementDates(val startDate: String, val endDate: String?, val isActive: Boolean)

/** SH-212: [entries] written back into [sources] (see [TimelineSources] for where each kind goes). */
object TimelineWriter {
    /** What changed, part by part; null parts stay as they are. */
    data class Written(
        val engagement: EngagementDates? = null,
        val financial: FinancialEngagement? = null,
        val staff: StaffRecord? = null,
        val notes: List<TimelineNote>? = null,
        /** The day after a planned end of cooperation (functions/timeline.js ends the engagement then); null: none planned. */
        val plannedEndOn: LocalDate? = null,
        /** SH-232: the person's periods without pay (empty: none any more). */
        val unpaid: List<com.evoapps.evoshift.domain.budget.UnpaidPeriod>? = null,
        /** SH-232: the labour inclusion setting (empty: the default, the setting goes). */
        val labour: List<com.evoapps.evoshift.domain.budget.LabourInclusionPeriod>? = null
    ) {
        val isEmpty: Boolean get() = engagement == null && financial == null && staff == null && notes == null && unpaid == null && labour == null
    }

    fun write(userId: String, workplaceId: String, sources: TimelineSources, edited: List<TimelineEntry>, today: LocalDate, reliefs: List<PayrollFact>): Written {
        // SH-250: an edit that leaves two entries saying the same one after another stores one.
        val entries = TimelineMerge.merged(edited)
        fun of(kind: TimelineKind) = entries.filter { it.kind == kind }.sortedWith(compareBy(nullsFirst()) { it.from })

        // Hire and end: the engagement. An end on or after today is planned: the engagement stays
        // active until then (functions/timeline.js ends it the day after).
        val engagement = sources.engagement?.let { e ->
            // The same day as stored stays as stored (older profiles keep a whole timestamp there).
            val hireDay = of(TimelineKind.HIRE).firstOrNull()?.from
            val start = if (hireDay == null || hireDay == day(e.startDate)) e.startDate else hireDay.toString()
            val endEntry = of(TimelineKind.END).firstOrNull()
            val end = endEntry?.from
            // SH-234: the stale end of a re-activated engagement (not shown) stays as stored.
            val keptEnd = e.endDate.takeIf { endEntry == null && sources.endIsStale(today) }
            val dates = EngagementDates(
                startDate = start,
                endDate = if (end != null && end == day(e.endDate)) e.endDate else end?.toString() ?: endEntry?.let { e.endDate } ?: keptEnd,
                isActive = if (endEntry == null) true else end != null && end >= today
            )
            dates.takeIf { it != EngagementDates(e.startDate, e.endDate, e.isActive) }
        }
        val endDay = of(TimelineKind.END).firstOrNull()?.from
        val plannedEndOn = endDay?.takeIf { it >= today }?.next()

        // Contract, pay and payroll facts: the dated histories of the pay terms.
        val financial = sources.financial?.let { terms ->
            val contracts = of(TimelineKind.CONTRACT).map {
                ContractPeriod(it.from?.toString().orEmpty(), it.contractTypeId ?: terms.contractTypeId, it.declaredHours)
            }.ifEmpty { terms.contractPeriods }
            val pays = of(TimelineKind.PAY).mapNotNull { e -> e.pay?.let { RatePeriod(e.from?.toString().orEmpty(), it) } }
                .ifEmpty { terms.ratePeriods }
            var profiles = terms.profilePeriods
            val before = TimelineReader.read("", "", sources, today, reliefs)
            fun spansOf(list: List<TimelineEntry>) = list.map { FactSpans.Span(it.from, it.until) }.sortedWith(compareBy(nullsFirst()) { it.from })
            val student = spansOf(of(TimelineKind.STUDENT))
            if (student != spansOf(before.entries.filter { it.kind == TimelineKind.STUDENT })) {
                profiles = FactSpans.write(profiles, PayrollFact.STUDENT, student, terms.payrollProfile)
            }
            (reliefs + entries.mapNotNull { it.fact }).distinct().forEach { fact ->
                val now = spansOf(of(TimelineKind.TAX_RELIEF).filter { it.fact == fact })
                if (now != spansOf(before.entries.filter { it.kind == TimelineKind.TAX_RELIEF && it.fact == fact })) {
                    profiles = FactSpans.write(profiles, fact, now, terms.payrollProfile)
                }
            }
            val rebuilt = terms.copy(
                compensation = pays.last().compensation,
                compensationEffectiveFrom = pays.last().effectiveFrom.ifEmpty { null },
                rateHistory = pays.dropLast(1),
                payrollProfile = profiles.last().profile,
                payrollProfileEffectiveFrom = profiles.last().effectiveFrom.ifEmpty { null },
                profileHistory = profiles.dropLast(1),
                contractTypeId = contracts.last().contractTypeId,
                declaredNumberOfHours = contracts.last().declaredNumberOfHours,
                contractEffectiveFrom = contracts.last().effectiveFrom.ifEmpty { null },
                contractHistory = contracts.dropLast(1)
            ).normalizedOn(today.toString())
            // Written when anything changed, also when only today's current fields moved on.
            rebuilt.takeIf { it != terms }
        }

        // OHS training and medical exam: the HR dates with their history.
        val records = StaffCheck.entries.flatMap { check ->
            of(TimelineKind.of(check)).map { StaffCheckRecord(check, it.from, it.until) }
        }
        val staffBefore = sources.staff
        val staff = StaffRecord.of(
            userId = userId,
            workplaceId = workplaceId,
            records = records,
            today = today,
            base = staffBefore?.copy(userId = userId, workplaceId = workplaceId)
        ).takeIf { candidate ->
            val old = StaffCheck.entries.flatMap { staffBefore?.records(it).orEmpty() }
            val new = StaffCheck.entries.flatMap { candidate.records(it) }
            old.toSet() != new.toSet()
        }

        val notes = of(TimelineKind.NOTE).mapNotNull { e ->
            val on = e.from ?: return@mapNotNull null
            TimelineNote(e.noteId ?: "", on, e.text.orEmpty().trim())
        }.takeIf { it.toSet() != sources.notes.toSet() }

        // SH-232: working without pay: the person's periods, as the entries say.
        val unpaid = sources.unpaid?.let { before ->
            val now = com.evoapps.evoshift.domain.budget.UnpaidWork.normalized(
                of(TimelineKind.UNPAID).map { com.evoapps.evoshift.domain.budget.UnpaidPeriod(it.from?.toString(), it.until?.toString()) }
            )
            now.takeIf { it != com.evoapps.evoshift.domain.budget.UnpaidWork.normalized(before) }
        }
        // SH-232: not counted: the setting rewritten when the spans changed (the default follows the days without pay).
        val labour = sources.labour?.let { source ->
            val after = source.copy(unpaid = unpaid ?: source.unpaid)
            val hire = day(sources.engagement?.startDate)
            fun spansOf(list: List<TimelineEntry>) = list.map { FactSpans.Span(it.from, it.until) }.sortedWith(compareBy(nullsFirst()) { it.from })
            val wanted = spansOf(of(TimelineKind.NOT_COUNTED))
            if (wanted == spansOf(LabourSpans.of(source, today, hire).map { TimelineEntry(TimelineKind.NOT_COUNTED, it.from, it.until) })) null
            else LabourSpans.write(after, wanted, today, hire).takeIf { it != source.periods }
        }

        return Written(engagement, financial, staff, notes, plannedEndOn, unpaid, labour)
    }
}
